/**
 * iter-362 — Sponsor Enquire lead-capture popover.
 *
 * Rendered from inside SponsorBadge (rich-catalogue variant, i.e. the Vendor
 * Publish placement). Captures name + email + phone + message and POSTs a
 * `lead` event to /sponsors/{id}/track. On success we show a thank-you
 * toast AND open the sponsor's CTA URL in a new tab so the lead lands
 * on the sponsor's site immediately.
 *
 * AI GUARDRAIL: this component only writes a lead into sponsor_campaigns.events;
 * lead data is NEVER surfaced to AI prompts. Admin retrieves it via
 * /sponsors/{id}/leads.csv.
 */
import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Sparkles } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SponsorEnquireDialog({
  open, onOpenChange,
  sponsorId, sponsorName, sponsorCtaUrl,
  projectId, worksheetId, sourceZone,
  publicToken = null, // iter-363 — anonymous public-share submission
}) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const reset = () => setForm({ name: '', email: '', phone: '', message: '' });

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setSubmitting(true);
    const token = localStorage.getItem('token');
    try {
      if (publicToken) {
        // Anonymous submit through the public track endpoint.
        let visitorId = '';
        try { visitorId = localStorage.getItem('bella_visitor_id') || ''; } catch { /* ignore */ }
        await axios.post(`${API}/public/sponsors/${sponsorId}/track`, {
          event: 'lead',
          token: publicToken,
          lead_name: form.name.trim(),
          lead_email: form.email.trim(),
          context: (form.phone ? `phone=${form.phone.trim()}; ` : '') + (form.message.trim() || ''),
          source_zone: sourceZone,
          visitor_id: visitorId,
        });
      } else {
        await axios.post(`${API}/sponsors/${sponsorId}/track`, {
          event: 'lead',
          lead_name: form.name.trim(),
          lead_email: form.email.trim(),
          context: (form.phone ? `phone=${form.phone.trim()}; ` : '') + (form.message.trim() || ''),
          source_zone: sourceZone,
          project_id: projectId,
          worksheet_id: worksheetId,
        }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      }
      toast.success(`Thanks, ${form.name.split(' ')[0] || 'there'}! ${sponsorName} will be in touch shortly.`);
      reset();
      onOpenChange(false);
      // Open the sponsor site in a new tab so the lead lands on the CTA immediately.
      if (sponsorCtaUrl) {
        window.open(sponsorCtaUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not submit — please try again');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent className="max-w-md" data-testid="sponsor-enquire-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles size={16} className="text-amber-600" />
            Enquire with {sponsorName}
          </DialogTitle>
          <DialogDescription className="text-xs text-stone-500">
            Leave your details and {sponsorName}&apos;s team will reach out. Your enquiry is not shared with our AI or included in your BOQ costing.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs">Your Name *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Rohan Wagh"
              data-testid="sponsor-enquire-name"
              required
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs">Email *</Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
              placeholder="you@example.com"
              data-testid="sponsor-enquire-email"
              required
            />
          </div>
          <div>
            <Label className="text-xs">Phone (optional)</Label>
            <Input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
              placeholder="+91 98xxxxxxxx"
              data-testid="sponsor-enquire-phone"
            />
          </div>
          <div>
            <Label className="text-xs">What are you looking for? (optional)</Label>
            <Textarea
              rows={3}
              value={form.message}
              onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
              placeholder="e.g. Hafele wardrobe hinges for a 3BHK residence in Wakad, Pune."
              data-testid="sponsor-enquire-message"
            />
          </div>
          <p className="text-[10px] text-stone-400 italic">
            Sponsored partnership — this does not affect your BOQ rates or specifications.
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={submitting}
              data-testid="sponsor-enquire-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="sponsor-enquire-submit"
            >
              {submitting ? (<><Loader2 size={14} className="mr-1 animate-spin" /> Sending…</>) : 'Send Enquiry'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
